import { useEffect, useRef } from 'react';
import { AnimatePresence } from 'motion/react';
import { useStickToBottom } from 'use-stick-to-bottom';
import { useAgentContext } from '@Providers';

import { sendCustomEvent } from '@Analytics';
import { useAgents } from '@CustomHooks';
import { AgentOptionItem, FeedbackSignal, GuidedJourney, OSAMessage } from '@Models';
import { useAgentInteractions, useAttachments, useGuidedJourneys } from '../../../CustomHooks/Agents';
import useBrowserNotification, { NotificationBannerCtx } from '../../../CustomHooks/Agents/useBrowserNotification';
import useChatManagement from '../../../CustomHooks/Agents/useChatManagement';
import { sendSignal } from '../../../Helpers/Agents/signals';
import NotificationBanner from '../NotificationBanner';
import { OnboardingWizard, PreferencesModal, useOnboardingPresenter } from '../Onboarding';
import AttachmentDropZone from './AttachmentDropZone';
import { CHOICE_SEPARATOR } from './ChoiceGrid';
import Composer from './Composer';
import MessageList from './MessageList';
import MessageListSkeleton from './MessageListSkeleton';
import ScrollToBottomButton from './ScrollToBottomButton';
import Welcome from './Welcome';

// Module-scoped so scroll position survives ConversationView unmount on panel close/reopen.
const conversationScrollPositions = new Map<string, number>();

const ConversationView = () => {
  const {
    activeChatId,
    selectedAgent,
    initialMessage,
    setInitialMessage,
    pendingJourneyId,
    setPendingJourneyId,
    isPreferencesOpen,
    closePreferences,
  } = useAgentContext();
  const { messages, interactionsQuery, isAccessDenied } = useAgentInteractions(activeChatId);
  const {
    sendUserMessage,
    executeJourney,
    stopGeneration,
    isGenerating,
    isThinking,
    thinkingSteps,
    thinkingSeconds,
    turnTimestamp,
    startedAtMs,
  } = useChatManagement();
  const { data: agents } = useAgents();
  const { data: journeys, isLoading: isLoadingJourneys } = useGuidedJourneys();
  const attachments = useAttachments();
  const attachmentsEnabled = selectedAgent?.attachments_enabled === true;
  const { clear: clearAttachments } = attachments;

  // Files not sent yet belong to the conversation and the agent they were attached in.
  const previousChatId = useRef(activeChatId);
  useEffect(() => {
    const previous = previousChatId.current;
    previousChatId.current = activeChatId;
    // A first send gives a new conversation its id, and a failed one takes it back: neither is a move.
    if (previous && activeChatId && previous !== activeChatId) clearAttachments();
  }, [activeChatId, clearAttachments]);
  useEffect(() => {
    clearAttachments();
  }, [selectedAgent?.source_name, clearAttachments]);

  const {
    preferences,
    questions: onboardingQuestions,
    isOpen: isWizardOpen,
    isPersonalizeVisible,
    open: openWizard,
    close: closeWizard,
    skip: skipWizard,
    complete: completeWizard,
  } = useOnboardingPresenter(selectedAgent?.source_name);

  // initial:false defers the anchor so our effect chooses restore-vs-bottom without the lib racing first.
  const { scrollRef, contentRef, scrollToBottom, isAtBottom } = useStickToBottom({
    initial: false,
    resize: 'smooth',
  });

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !activeChatId) return undefined;
    let raf: number | null = null;
    const onScroll = () => {
      if (raf !== null) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        const { current } = scrollRef;
        if (current) conversationScrollPositions.set(activeChatId, current.scrollTop);
      });
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      if (raf !== null) cancelAnimationFrame(raf);
      el.removeEventListener('scroll', onScroll);
    };
  }, [activeChatId, scrollRef]);

  const restoredChatIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!activeChatId) {
      restoredChatIdRef.current = null;
      return;
    }
    if (interactionsQuery.isLoading) return;
    if (messages.length === 0) return;
    if (restoredChatIdRef.current === activeChatId) return;
    restoredChatIdRef.current = activeChatId;
    const saved = conversationScrollPositions.get(activeChatId);
    if (saved !== undefined) {
      requestAnimationFrame(() => {
        const el = scrollRef.current;
        if (el) el.scrollTop = saved;
      });
    } else {
      Promise.resolve(scrollToBottom('auto')).catch(() => {});
    }
  }, [activeChatId, interactionsQuery.isLoading, messages.length, scrollRef, scrollToBottom]);

  // Fires an OS-level notification when the agent finishes thinking while the
  // tab is hidden. Also drives the in-app banner that asks for permission.
  const notificationCtx = useBrowserNotification(isThinking);
  const isExecutingJourneyRef = useRef(false);

  // Email deep-link: `pendingJourneyId` set by the journey-token redemption
  // path. Auto-execute and clear when journeys load.
  useEffect(() => {
    if (!pendingJourneyId || !journeys) return;
    if (isExecutingJourneyRef.current) return;
    const journey = journeys.find((j) => j.id === pendingJourneyId);
    if (!journey) return;
    isExecutingJourneyRef.current = true;
    executeJourney(journey).finally(() => {
      isExecutingJourneyRef.current = false;
      setPendingJourneyId(null);
    });
  }, [pendingJourneyId, journeys, executeJourney, setPendingJourneyId]);

  useEffect(() => {
    if (!initialMessage || !selectedAgent) return;
    sendUserMessage(initialMessage).catch(() => {});
    setInitialMessage(null);
  }, [initialMessage, selectedAgent, sendUserMessage, setInitialMessage]);

  const handleSelectJourney = (journey: GuidedJourney) => {
    executeJourney(journey).catch(() => {});
  };

  const handleSelectOption = (_message: OSAMessage, option: AgentOptionItem) => {
    sendUserMessage(option.label).catch(() => {});
  };

  // Multi-select confirm: submit the chosen labels as one message (newline-
  // joined) so the selection round-trips through message text and persists —
  // mirrors the single-select path and how the backend derives the selection.
  const handleConfirmSelection = (_message: OSAMessage, selected: AgentOptionItem[]) => {
    if (selected.length === 0) return;
    sendUserMessage(selected.map((o) => o.label).join('\n')).catch(() => {});
  };

  // Choice-grid confirm: one `<row label> → <answer>` line per ANSWERED row,
  // as a single message. Unanswered rows contribute nothing — they are still
  // unanswered, and these answers land in an audit record.
  const handleConfirmChoices = (
    _message: OSAMessage,
    answered: { row: AgentOptionItem; choice: { id: string; label: string } }[],
  ) => {
    if (answered.length === 0) return;
    const lines = answered.map(({ row, choice }) => `${row.label}${CHOICE_SEPARATOR}${choice.label}`);
    sendUserMessage(lines.join('\n')).catch(() => {});
  };

  // Composer only: D8's denominator is questions the user actually typed.
  const handleSend = (text: string): Promise<boolean> => {
    // sendUserMessage mints a fresh id when there is none or the current one is 403-denied.
    sendCustomEvent(
      'AGENT_MESSAGE_SENT',
      activeChatId && !isAccessDenied ? { conversationId: activeChatId } : { isFirstTurn: true },
    );
    const outgoing = attachmentsEnabled ? attachments.startSend() : null;
    sendUserMessage(text, { upload: outgoing?.upload }).catch(() => {});
    return outgoing?.settled ?? Promise.resolve(true);
  };

  const handleStop = () => stopGeneration() ?? null;

  // Re-sends the prompt that produced this turn; backend creates a fresh interaction (no mutation).
  const handleRegenerate = (assistant: OSAMessage) => {
    // Defensive: MessageList's `optionsActive` already unmounts the button while generating.
    if (isGenerating) return;
    const idx = messages.findIndex((m) => m.id === assistant.id);
    if (idx <= 0) return;
    for (let i = idx - 1; i >= 0; i -= 1) {
      if (messages[i].role === 'user') {
        sendSignal(assistant.conversationId, assistant.interactionTimestamp, FeedbackSignal.REGENERATE);
        sendUserMessage(messages[i].content).catch(() => {});
        return;
      }
    }
  };

  const isLoadingThread = activeChatId !== null && interactionsQuery.isLoading && messages.length === 0;
  const hasThread = (activeChatId !== null && messages.length > 0) || isThinking;
  const safeJourneys: GuidedJourney[] = journeys ?? [];
  const hasAgents = (agents?.length ?? 0) > 0;

  return (
    <NotificationBannerCtx.Provider value={notificationCtx}>
      <AttachmentDropZone
        enabled={attachmentsEnabled && !isGenerating}
        onDrop={attachments.add}
        className="flex h-full min-w-0 flex-col">
        {isThinking && (
          <div className="px-4 pt-2">
            <NotificationBanner />
          </div>
        )}
        <div className="relative flex-1 min-w-0 overflow-hidden">
          <div ref={scrollRef} className="h-full overflow-y-auto overflow-x-hidden">
            <div ref={contentRef}>
              {isLoadingThread ? (
                <MessageListSkeleton />
              ) : hasThread ? (
                <MessageList
                  messages={messages}
                  isThinking={isThinking}
                  isGenerating={isGenerating}
                  thinkingSteps={thinkingSteps}
                  thinkingSeconds={thinkingSeconds}
                  turnTimestamp={turnTimestamp}
                  thinkingStartedAtMs={startedAtMs}
                  onSelectOption={handleSelectOption}
                  onConfirmSelection={handleConfirmSelection}
                  onConfirmChoices={handleConfirmChoices}
                  onRegenerate={handleRegenerate}
                />
              ) : (
                <Welcome
                  preferredName={preferences?.preferredName ?? null}
                  journeys={safeJourneys}
                  isLoadingJourneys={isLoadingJourneys}
                  isPersonalizeVisible={isPersonalizeVisible}
                  onPersonalize={openWizard}
                  onSelectJourney={handleSelectJourney}
                />
              )}
            </div>
          </div>
          {hasThread && <ScrollToBottomButton isAtBottom={isAtBottom} onScrollToBottom={() => scrollToBottom()} />}
        </div>

        <Composer
          onSend={handleSend}
          isGenerating={isGenerating}
          canStop={isThinking}
          onStop={handleStop}
          disabled={!hasAgents || !selectedAgent}
          attachments={attachmentsEnabled ? attachments : undefined}
        />
      </AttachmentDropZone>

      <AnimatePresence>
        {isWizardOpen && onboardingQuestions.length > 0 && (
          <OnboardingWizard
            questions={onboardingQuestions}
            onComplete={closeWizard}
            onSkip={skipWizard}
            onSave={completeWizard}
          />
        )}
      </AnimatePresence>

      <PreferencesModal
        questions={onboardingQuestions}
        sourceName={selectedAgent?.source_name}
        isOpen={isPreferencesOpen}
        onClose={closePreferences}
      />
    </NotificationBannerCtx.Provider>
  );
};

export default ConversationView;
